import React, { useState, useEffect, useRef } from "react";
import { Search, Compass, ShieldCheck, CheckCircle2, UserCheck, Play } from "lucide-react";

export const ProcessTimeline = () => {
  const [activeStep, setActiveStep] = useState(0);
  const containerRef = useRef(null);

  const steps = [
    {
      title: "Discover",
      icon: Search,
      desc: "Audit electrical/mechanical scope, budget tolerances, SLA parameters, and security requirements with core clients."
    },
    {
      title: "Source",
      icon: Compass,
      desc: "Deploy automated indexing engines across global databases to align certified critical시설 engineers."
    },
    {
      title: "Screen",
      icon: ShieldCheck,
      desc: "Execute structured mechanical diagnostic tests, medium voltage electrical checks, and background SOC-2 audits."
    },
    {
      title: "Match",
      icon: UserCheck,
      desc: "Shortlist the absolute top 3 operations candidates, with comprehensive alignment analytics metrics reports."
    },
    {
      title: "Deploy",
      icon: Play,
      desc: "Expedite travel clearance visas, local site integration, safety protocols, and operational transition logs."
    },
    {
      title: "Support",
      icon: CheckCircle2,
      desc: "Coordinate continuous Level 1-5 support logs, training updates, and long-term retention advisory programs."
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Calculate how far down the section has scrolled relative to the viewport
      if (rect.top < viewportHeight * 0.8 && rect.bottom > 0) {
        const totalHeight = rect.height;
        const progress = Math.max(0, Math.min(1, (viewportHeight * 0.5 - rect.top) / totalHeight));
        const stepIdx = Math.min(steps.length - 1, Math.floor(progress * steps.length));
        setActiveStep(stepIdx);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [steps.length]);

  return (
    <section 
      ref={containerRef}
      data-testid="process-timeline-section"
      className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16"
    >
      <div className="mb-16 text-center lg:text-left">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#65FFB6]">
          PIPELINE METRICS
        </span>
        <h2 className="text-3xl md:text-5xl font-outfit font-bold tracking-tight text-white mt-3">
          Our Recruitment Process
        </h2>
        <p className="text-slate-400 font-manrope text-base max-w-2xl mt-4">
          A rigid, six-stage deployment timeline designed to deliver zero-defect personnel to mission-critical infrastructure networks.
        </p>
      </div>

      {/* Timeline Path (Flex layout that wraps on mobile, horizontal on desktop) */}
      <div 
        data-testid="process-timeline-flow"
        className="relative flex flex-col md:flex-row justify-between items-start gap-8 md:gap-4 overflow-visible"
      >
        {/* Connection line background (desktop only) */}
        <div className="absolute top-12 left-6 right-6 h-[1.5px] bg-white/10 hidden md:block z-0">
          {/* Glowing active line segment */}
          <div 
            className="h-full bg-gradient-to-r from-[#65FFB6] to-transparent transition-all duration-500 ease-out"
            style={{ width: `${(activeStep / (steps.length - 1)) * 100}%` }}
          ></div>
        </div>

        {steps.map((step, idx) => {
          const IconComponent = step.icon;
          const isActive = idx <= activeStep;
          const isCurrent = idx === activeStep;

          return (
            <div 
              key={step.title}
              data-testid={`process-step-${idx}`}
              onMouseEnter={() => setActiveStep(idx)}
              className="flex flex-row md:flex-col items-start gap-4 md:gap-5 md:w-1/6 relative z-10 group cursor-pointer"
            >
              {/* Node Indicator circle */}
              <div 
                className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all duration-300 relative shrink-0 ${
                  isCurrent 
                    ? "bg-[#0B1F1A] border-[#65FFB6] text-[#65FFB6] shadow-[0_0_15px_rgba(101,255,182,0.4)] scale-110" 
                    : isActive 
                      ? "bg-black border-[#65FFB6]/50 text-[#65FFB6]" 
                      : "bg-slate-950 border-white/10 text-slate-500"
                }`}
              >
                <IconComponent className="w-4.5 h-4.5" />
                
                {/* Glowing beacon for the active step */}
                {isCurrent && (
                  <span className="absolute -inset-1 rounded-full border border-[#65FFB6]/40 animate-ping opacity-60"></span>
                )}
              </div>

              {/* Step Text Contents */}
              <div className="flex flex-col items-start text-left">
                <span className="text-[10px] font-mono text-emerald-400/80 uppercase tracking-widest">
                  STAGE 0{idx + 1}
                </span>
                <h3 className={`text-lg font-outfit font-semibold mt-1 transition-colors duration-200 ${
                  isCurrent ? "text-[#65FFB6]" : "text-white"
                }`}>
                  {step.title}
                </h3>
                <p className="text-xs sm:text-sm font-manrope text-slate-400 mt-2 leading-relaxed max-w-[200px]">
                  {step.desc}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
